import { useQuery } from '@apollo/client';
import { Create } from '@mui/icons-material';
import { Button, Grid, Icon, Paper, Pagination } from '@mui/material';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import PERMISSION_QUERY from 'gql/queries/PermissionProvider/getPermissions.gql';

import ErrorBoundary from 'app/components/ErrorBoundary';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import { RichList } from 'app/components/RichList';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { PermissionItem } from 'app/components/RichList/items/PermissionProvider/PermissionItem';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { PermissionModal } from './PermissionsModal';

export function PermissionList() {
  const permissionAuthor = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const permissionsQuery = useQuery<any, any>(PERMISSION_QUERY, { fetchPolicy: 'network-only' });
  const { refetch } = permissionsQuery;

  const handlePagination = (event, page: number) => {
    const offset = (page - 1) * 10;
    refetch({ offset: offset });
  };

  return (
    <GQLGuard
      query={permissionsQuery}
      onError={error => {
        dispatch(
          AlertActions.error(`${t(translations.pages.managePermissionsSettings.errorLoading())}`)
        );
        navigate('/', { replace: true });
        return null;
      }}
    >
      {data => (
        <>
          <ErrorBoundary boundaryTag="roleDetailBoundary">
            <Modal
              open={createModalOpen}
              onClose={(event, reason) => {
                if (reason && reason === 'backdropClick') return;
                setCreateModalOpen(false);
              }}
              headline={t(translations.pages.managePermissionsSettings.permissionCreation())}
              onCancel={() => {
                setCreateModalOpen(false);
              }}
              disabledCancel={disabledCancel}
            >
              <PermissionModal
                onCancel={() => setCreateModalOpen(false)}
                closeHandler={() => {
                  setCreateModalOpen(false);
                }}
                setDisabledCancel={setDisabledCancel}
              />
            </Modal>
          </ErrorBoundary>
          <RichList
            controls={
              <>
                <Button
                  startIcon={<Create />}
                  variant="contained"
                  className="manage-companies__actions--download"
                  onClick={() => setCreateModalOpen(true)}
                >
                  {t(translations.pages.managePermissionsSettings.createPermission())}
                </Button>
              </>
            }
            onFilterChange={refetch}
            filters={[
              {
                field: 'authorName',
                label: t(translations.pages.managePermissionsSettings.permission()),
                stateHandler: permissionAuthor
              }
            ]}
            staticHeading={
              <Grid
                container
                className="permission-settings__list-heading"
                component={Paper}
                square
              >
                <Grid item md={1} className="permission-settings__list-heading__item">
                  {t(translations.common.id())}
                </Grid>
                <Grid item md={3} className="permission-settings__list-heading__item">
                  {t(translations.pages.managePermissionsSettings.authorRole())}
                </Grid>
                <Grid item md={7} className="permission-settings__list-heading__item">
                  {t(translations.pages.managePermissionsSettings.recipients())}
                </Grid>
                <Grid item md={1} className="permission-settings__list-heading__item">
                  {t(translations.pages.managePermissionsSettings.actions())}
                </Grid>
              </Grid>
            }
          >
            {data.permissions.length === 0 ? (
              <NoItems
                icon={<Icon className="fas fa-info-circle" />}
                message={t(translations.pages.managePermissionsSettings.noItemFound())}
              />
            ) : (
              <Grid container className="permission-settings__list" component={Paper}>
                {data.permissions.map(permission => (
                  <ErrorBoundary key={permission.id} boundaryTag="ientitlementItemBoundary">
                    <PermissionItem permission={permission} key={permission.id} />
                  </ErrorBoundary>
                ))}
              </Grid>
            )}
          </RichList>
          {Math.floor(data.permissionsCount / 10) > 0 && (
            <Grid container className="permission-settings__pagination">
              <Pagination
                count={Math.ceil(data.permissionsCount / 10)}
                onChange={handlePagination}
                variant="outlined"
                shape="rounded"
                showFirstButton
                showLastButton
                size="medium"
                color="primary"
                siblingCount={2}
                boundaryCount={2}
              />
            </Grid>
          )}
        </>
      )}
    </GQLGuard>
  );
}
